import AppDialog from '../../components/app_dialog'
import RowMenu from '../../components/row_menu'
import { Badge, Button, Text, TextArea, TextField, Tooltip, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../../components/data_table'
import { ArrowsClockwise } from '@phosphor-icons/react'
import { useSnackbar } from '../../hooks/use_snackbar'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertToDateTime } from '../../helpers/convert_date'
import useStore from '../../hooks/use_store'
import useAuth from '../../hooks/use_auth'
import axios from 'axios'
import { Fragment, useState } from 'react'
import capitalize from '../../helpers/capitalize'

const POLL_TIME = 5000 // 5 seconds

const TERMINAL_EXTERNAL_STATUSES = new Set(['deployed', 'published', 'deployment-failed', 'failed', 'promote-failed'])

const needsPolling = (snapshots = []) =>
  snapshots.some((snapshot) => {
    if (!snapshot.is_completed && !snapshot.is_failed) return true
    const status = snapshot.external_status
    return status != null && !TERMINAL_EXTERNAL_STATUSES.has(status)
  })

export default function Snapshots({ queryKey, cycle }) {
  const { data, isLoading, error } = useQuery(queryKey, {
    refetchInterval: (data) => (needsPolling(data?.data) ? POLL_TIME : false),
    // Pause while the tab is hidden / window unfocused.
    refetchIntervalInBackground: false,
  })
  const [detailModalOpenForSnapshot, setDetailModalOpenForSnapshot] = useState(null)
  const [promoteSnapshotId, setPromoteSnapshotId] = useState(null)
  const [airlineGroupCode, setAirlineGroupCode] = useState('')
  const [pendingSendIds, setPendingSendIds] = useState([])
  const groupCodeMatches = airlineGroupCode !== '' && airlineGroupCode === cycle?.airline?.group_code
  const snapshots = data?.data || []
  const { addMessage } = useSnackbar()
  const queryClient = useQueryClient()
  const { deleteModel } = useStore()
  const { user } = useAuth()
  const markSendPending = (snapshotID) =>
    setPendingSendIds((ids) => (ids.includes(snapshotID) ? ids : [...ids, snapshotID]))
  const clearSendPending = (snapshotID) => setPendingSendIds((ids) => ids.filter((id) => id !== snapshotID))
  const deleteSnapshot = (snapshot) => {
    deleteModel.mutate(
      {
        endpoint: 'snapshots',
        id: snapshot.id,
        deleteNoun: 'snapshot',
        deleteName: snapshot.filename,
      },
      { onSuccess: () => queryClient.invalidateQueries(queryKey) }
    )
  }
  const sendSnapshotToViasat = useMutation(
    ({ snapshotID, publish = null }) => {
      return axios.post(`snapshots/${snapshotID}/send-to-viasat`, null, {
        params: {
          publish,
        },
      })
    },
    {
      onSuccess: () => {
        addMessage('Snapshot sent to Viasat API successfully.')
        queryClient.invalidateQueries(queryKey)
      },
      onError: (error, { snapshotID }) => {
        clearSendPending(snapshotID)
        addMessage(`Error sending snapshot to Viasat API: ${error.response?.data?.message || error.message}`, 'error')
        queryClient.invalidateQueries(queryKey)
      },
    }
  )

  const promoteSnapshotToViasat = useMutation(
    ({ snapshotID }) => {
      return axios.post(`snapshots/${snapshotID}/promote`)
    },
    {
      onSuccess: () => {
        addMessage('Snapshot "Promote" to Viasat API sent successfully.')
        queryClient.invalidateQueries(queryKey)
      },
      onError: (error) => {
        addMessage(`Error promoting snapshot to Viasat API: ${error.response?.data?.message || error.message}`, 'error')
        queryClient.invalidateQueries(queryKey)
      },
    }
  )

  const extractExternalErrors = (snapshot) => {
    let externalErrors = null
    const json = snapshot?.external_error ?? '<empty json>'
    try {
      externalErrors = JSON.parse(json)
      // if errors is double-nested, extract the lowest errors and re-format
      if (externalErrors?.errors?.[0]?.errors) {
        externalErrors.errors = externalErrors.errors[0].errors.map((error) => ({ message: error }))
      }

      if (externalErrors?.fault) {
        externalErrors.errors = [externalErrors.fault]
      }
    } catch (ex) {
      return ['Could not parse errors, invalid JSON in error message']
    }

    return externalErrors?.errors?.map((error) => error.message)
  }

  const allowPromote = (snapshot) => {
    if (!snapshot.external_status_updates) {
      return false
    }

    if (snapshot.external_status !== 'deployed') {
      return false
    }

    // pull statuses only for current snapshotId.
    // snapshotId would change if a "test" snapshot was "published"
    const statusesForCurrentSnapshotId = snapshot.external_status_updates
      .filter((update) => update.snapshotId === snapshot.external_snapshot_id)
      .map((update) => update.status)

    return statusesForCurrentSnapshotId.includes('published')
  }

  const columns = [
    {
      key: 'filename',
      header: 'Filename',
      render: (data) =>
        !data.is_completed && !data.is_failed ? <ArrowsClockwise size={20} className='animate-spin' /> : data?.filename,
    },
    { key: 'created_at', header: 'Created At', render: (data) => convertToDateTime(data?.created_at) },
    {
      key: 'status',
      header: 'Status',
      render: (data) => (
        <div className='flex space-x-2'>
          <Badge tone={data.is_completed ? 'success' : data.is_failed ? 'error' : 'warning'}>
            {data.is_completed ? 'Complete' : data.is_failed ? 'Failed' : `In Progress: ${parseInt(data.progress)}%`}
          </Badge>
          {Boolean(data.generated_with_errors) && (
            <Tooltip
              trigger={<Badge tone='error'>Generated with Errors</Badge>}
              title={`This snapshot was generated by ${data?.user?.email} despite errors being present.`}
            />
          )}
        </div>
      ),
    },
    {
      key: 'external_status',
      header: 'External Status',
      render: (data) =>
        data.is_completed && (
          <div className='flex items-center justify-between size-full'>
            <Badge
              tone={
                data.external_status === null || data.external_status === 'created'
                  ? 'warning'
                  : ['deployment-failed', 'failed', 'promote-failed'].includes(data.external_status)
                    ? 'error'
                    : 'success'
              }
            >
              {data.external_status === null
                ? 'Ready to Send'
                : data.external_status === 'created'
                  ? 'Deploying'
                  : data.external_status.split('-').map(capitalize).join(' ')}
            </Badge>
            {data.external_status !== null && (
              <Button variant='secondary' onClick={() => setDetailModalOpenForSnapshot(data)}>
                Details
              </Button>
            )}
          </div>
        ),
    },
    {
      key: 'external_actions',
      header: 'External actions',
      render: (data) => (
        <div className='flex space-x-2'>
          {data.external_status === null && (
            <Button
              disabled={!data.is_completed || pendingSendIds.includes(data.id)}
              loading={pendingSendIds.includes(data.id)}
              onClick={() => {
                markSendPending(data.id)
                sendSnapshotToViasat.mutate({ snapshotID: data.id })
              }}
            >
              Push to preview server
            </Button>
          )}
          {data.external_status === 'deployed' && !allowPromote(data) && (
            <Button
              disabled={!data.is_completed || pendingSendIds.includes(data.id)}
              loading={pendingSendIds.includes(data.id)}
              onClick={() => {
                markSendPending(data.id)
                sendSnapshotToViasat.mutate({ snapshotID: data.id, publish: true })
              }}
            >
              Push to pre-prod server
            </Button>
          )}
          {allowPromote(data) && user?.has_unrestricted_edit_role && (
            <Button
              onClick={() => {
                setAirlineGroupCode('')
                setPromoteSnapshotId(data.id)
              }}
            >
              Promote
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: (data) => (
        <RowMenu
          label={`Actions for snapshot ${data.id}`}
          items={[
            {
              id: 'view-submission',
              label: 'View submission',
              disabled: !data.signed_url,
              onSelect: () => window.open(`${data.signed_url}`, '_blank'),
            },
            {
              id: 'delete',

              label: 'Delete',
              tone: 'danger',
              onSelect: () => deleteSnapshot(data),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <p
        className='mb-8 rounded-[var(--cs-radius-2xl)] border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-secondary)] px-5 py-4 text-[length:var(--cs-text-sm-font-size)] leading-[var(--cs-text-sm-line-height)] text-[color:var(--cs-color-foreground-secondary)]'
        role='note'
      >
        A submission captures this cycle’s metadata at a point in time. Create a new one after metadata changes. You can
        send a submission to Viasat for 7 days after it’s created; if it expires first, create another.
      </p>
      <DataTable
        caption='Snapshots'
        columns={columns}
        rows={snapshots ?? []}
        isLoading={isLoading}
        error={error}
        empty={<EmptyState title='No snapshots available for this cycle.' />}
        rowKey={(data) => String(data.id)}
      />
      {promoteSnapshotId !== null && (
        <AppDialog
          title='Promote submission'
          onClose={() => {
            setPromoteSnapshotId(null)
            setAirlineGroupCode('')
          }}
          confirmText='Promote'
          confirmVariant='destructive'
          confirmDisabled={!groupCodeMatches}
          onConfirm={() => {
            const snapshotID = promoteSnapshotId
            setPromoteSnapshotId(null)
            setAirlineGroupCode('')
            promoteSnapshotToViasat.mutate({ snapshotID })
          }}
        >
          <Text as='p' variant='sm' tone='secondary'>
            You're about to promote this submission. Are you sure you want to do this?
          </Text>
          <TextField
            label='Enter the group code to promote'
            value={airlineGroupCode}
            onChange={(e) => setAirlineGroupCode(e.target.value)}
            autoFocus
          />
        </AppDialog>
      )}
      {detailModalOpenForSnapshot !== null && (
        <AppDialog title='External API details' onClose={() => setDetailModalOpenForSnapshot(null)} size='lg'>
          <div className='grid grid-cols-2 gap-y-2'>
            <div className='font-bold'>Current Snapshot ID</div>
            <div>{detailModalOpenForSnapshot.external_snapshot_id}</div>
            <div className='font-bold'>Submitted By</div>
            <div>{detailModalOpenForSnapshot.submitted_by_user?.email ?? 'unknown'}</div>
            <div className='font-bold'>Filename</div>
            <div>{detailModalOpenForSnapshot.filename}</div>
          </div>

          <div className='mt-6 mb-2 text-lg font-bold'>Status Updates</div>
          <div className='grid grid-cols-[120px_220px_300px] gap-x-4 gap-y-2'>
            <div className='font-bold'>Status</div>
            <div className='font-bold'>Snapshot ID</div>
            <div className='font-bold'>Timestamp</div>
            {detailModalOpenForSnapshot.external_status_updates &&
              detailModalOpenForSnapshot.external_status_updates.map((update, index) => (
                <Fragment key={`${update.timestamp}-${update.status}-${index}`}>
                  <div className='flex gap-x-4'>
                    <Badge
                      tone={
                        ['deployment-failed', 'failed', 'promote-failed'].includes(update.status) ? 'error' : 'success'
                      }
                    >
                      {update.status.split('-').map(capitalize).join(' ')}
                    </Badge>
                    {update.url && (
                      <a className='text-blue-600 underline' href={update.url} target='_blank'>
                        visit
                      </a>
                    )}
                  </div>

                  <div>{update.snapshotId}</div>
                  <div>{convertToDateTime(update.timestamp)}</div>
                </Fragment>
              ))}
          </div>

          {detailModalOpenForSnapshot.external_error && (
            <>
              <div className='my-4 font-bold'>Errors returned by the API</div>
              <ul role='list' className='list-decimal'>
                {extractExternalErrors(detailModalOpenForSnapshot)?.map((line, index) => (
                  <li key={`${line}-${index}`} className='mx-10'>
                    {line}
                  </li>
                ))}
              </ul>
              <div>
                <p className='my-4 font-bold'>Raw JSON</p>
                <TextArea
                  label='External error'
                  hideLabel
                  disabled
                  value={detailModalOpenForSnapshot.external_error ?? '<empty json>'}
                />
              </div>
            </>
          )}
        </AppDialog>
      )}
    </>
  )
}
